/**
 * Runtime detection for mini-app host (MAX, Telegram, or plain browser).
 * Prefer URL/UA hints before loading either SDK. After load: MAX uses window.WebApp
 * (Bridge); Telegram uses window.Telegram.WebApp.
 */

export const PLATFORMS = {
  MAX: 'max',
  TELEGRAM: 'telegram',
  BROWSER: 'browser',
};

function getMaxWebApp() {
  return window.WebApp || null;
}

function getTelegramWebApp() {
  return window.Telegram?.WebApp || null;
}

function getLaunchParamString() {
  const hash = window.location.hash?.slice(1) || '';
  const search = window.location.search?.slice(1) || '';
  return `${hash}&${search}`;
}

/** Launch params Telegram injects into the URL (available before the SDK script). */
function hasTelegramLaunchParams() {
  return /(?:^|&)tgWebApp(?:Data|Version|Platform|ThemeParams)=/i.test(getLaunchParamString());
}

function hasTelegramUserAgent() {
  return /Telegram/i.test(navigator.userAgent || '');
}

/**
 * MAX launch params in URL hash/query (WebAppData, startapp) — available before Bridge script.
 * @see https://dev.max.ru/docs/webapps/validation
 */
export function hasMaxLaunchParams() {
  return /(?:^|&)(?:WebAppData|webAppData|startapp)=/i.test(getLaunchParamString());
}

/** Heuristic for MAX WebView before Bridge script runs (UA / host). */
function hasMaxUserAgentOrHost() {
  const ua = navigator.userAgent || '';
  if (/\bMAX\b/i.test(ua)) {
    return true;
  }
  return /\.max\.ru$/i.test(window.location.hostname) || window.location.hostname === 'max.ru';
}

function isMaxWebApp(webApp) {
  if (!webApp) {
    return false;
  }

  // MAX-only Bridge APIs (see https://dev.max.ru/docs/webapps/bridge)
  if (typeof webApp.openMaxLink === 'function') {
    return true;
  }
  if (typeof webApp.shareMaxContent === 'function') {
    return true;
  }

  const maxInitData = webApp.initData;
  const telegramInitData = getTelegramWebApp()?.initData;

  if (maxInitData && !telegramInitData) {
    return true;
  }

  if (webApp.initDataUnsafe?.user && !getTelegramWebApp()?.initDataUnsafe?.user) {
    return true;
  }

  // Native bridge exposes platform/version before the CDN script loads.
  const platform = webApp.platform;
  if (platform && ['ios', 'android', 'desktop', 'web'].includes(platform) && !hasTelegramLaunchParams()) {
    return true;
  }

  return false;
}

/**
 * Lightweight platform guess without loading SDKs.
 * @returns {'max' | 'telegram' | null}
 */
export function getPlatformHint() {
  if (hasTelegramLaunchParams()) {
    return PLATFORMS.TELEGRAM;
  }

  if (hasMaxLaunchParams()) {
    return PLATFORMS.MAX;
  }

  const webApp = getMaxWebApp();
  if (webApp && isMaxWebApp(webApp)) {
    return PLATFORMS.MAX;
  }

  if (hasMaxUserAgentOrHost()) {
    return PLATFORMS.MAX;
  }

  if (hasTelegramUserAgent()) {
    return PLATFORMS.TELEGRAM;
  }

  return null;
}

function logPlatformHint() {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  console.warn('[Platform] hint:', getPlatformHint());
}

/** True when the page is running inside the MAX messenger WebView. */
export function isMax() {
  if (getPlatformHint() === PLATFORMS.TELEGRAM) {
    return false;
  }

  if (hasMaxLaunchParams()) {
    return true;
  }

  const webApp = getMaxWebApp();
  if (webApp && isMaxWebApp(webApp)) {
    return true;
  }

  if (getPlatformHint() === PLATFORMS.MAX) {
    return true;
  }

  return false;
}

/** True when the page is running inside Telegram Mini App (and not MAX). */
export function isTelegram() {
  if (isMax()) {
    return false;
  }

  if (getTelegramWebApp()) {
    return true;
  }

  return getPlatformHint() === PLATFORMS.TELEGRAM;
}

/**
 * @returns {'max' | 'telegram' | 'browser'}
 */
export function getPlatform() {
  logPlatformHint();
  if (isMax()) {
    return PLATFORMS.MAX;
  }
  if (isTelegram()) {
    return PLATFORMS.TELEGRAM;
  }
  return PLATFORMS.BROWSER;
}
